import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Public as PublicIcon,
  Factory as FactoryIcon,
} from '@mui/icons-material';
import { TextInput } from '@/components';
import { CreateProductInput } from '@/services/product-catalogue/pc-api';
import { formatHTSCode } from '@/utils';
import ValidationAlerts, { ValidationIssue } from './validation-alerts';
import { tokens } from "@/tokens";

interface ExpandedProductFormProps {
  product: CreateProductInput;
  index: number;
  onUpdate: (index: number, updatedProduct: CreateProductInput) => void;
  requiresMetalPercentage?: boolean;
  issues: ValidationIssue[];
}

const ExpandedProductForm: React.FC<ExpandedProductFormProps> = ({
  product,
  index,
  onUpdate,
  requiresMetalPercentage,
  issues,
}) => {
  const [formData, setFormData] = useState<CreateProductInput>(product);

  useEffect(() => {
    setFormData(product);
  }, [product]);

  const handleFieldChange = (field: keyof CreateProductInput, value: any) => {
    const updatedProduct = { ...formData };
    
    if (field === 'metalPercentage') {
      updatedProduct[field] = value === '' ? undefined : Number(value);
    } else if (field === 'htsCode' && value) {
      // Format HTS code
      updatedProduct[field] = formatHTSCode(value.replace(/\D/g, ""));
    } else {
      updatedProduct[field] = value;
    }
    
    setFormData(updatedProduct);
    onUpdate(index, updatedProduct);
  };

  const getFieldError = (field: string) => {
    return issues.find(issue => issue.field === field && issue.type === 'error');
  };

  const getFieldWarning = (field: string) => {
    return issues.find(issue => issue.field === field && issue.type === 'warning');
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: 2, 
            borderRadius: tokens.radius.md,
            border: `1px solid ${tokens.border.default}`,
            backgroundColor: tokens.bg.hover,
          }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <DescriptionIcon sx={{ color: tokens.brand.primary }} />
              <Typography variant="subtitle1" fontWeight={tokens.fontWeight.bold} color={tokens.text.body}>
                Basic Information
              </Typography>
            </Stack>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Product SKU"
                  type="text"
                  value={formData.sku || ''}
                  handleChangeValue={(val) => handleFieldChange('sku', val)}
                  required
                  placeholder="e.g., ELEC-001"
                  error={!!getFieldError('sku')}
                  helperText={getFieldError('sku')?.message}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Product Name"
                  type="text"
                  value={formData.name || ''}
                  handleChangeValue={(val) => handleFieldChange('name', val)}
                  required
                  placeholder="e.g., Smartphone X12"
                  error={!!getFieldError('name')}
                  helperText={getFieldError('name')?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  label="Description"
                  type="text"
                  value={formData.description || ''}
                  handleChangeValue={(val) => handleFieldChange('description', val)}
                  multiline
                  rows={2}
                  placeholder="Optional product description"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Trade Information */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: 2, 
            borderRadius: tokens.radius.md,
            border: `1px solid ${tokens.border.default}`,
            backgroundColor: tokens.bg.hover,
          }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <PublicIcon sx={{ color: tokens.brand.primary }} />
              <Typography variant="subtitle1" fontWeight={tokens.fontWeight.bold} color={tokens.text.body}>
                Trade Information
              </Typography>
            </Stack>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Country of Origin"
                  type="text"
                  value={formData.countryOfOrigin || ''}
                  handleChangeValue={(val) => handleFieldChange('countryOfOrigin', val.toUpperCase())}
                  required
                  placeholder="e.g., US, CN, MX"
                  error={!!getFieldError('countryOfOrigin')}
                  helperText={getFieldError('countryOfOrigin')?.message || getFieldWarning('countryOfOrigin')?.message}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="HTS Code"
                  type="text"
                  value={formData.htsCode || ''}
                  handleChangeValue={(val) => handleFieldChange('htsCode', val)}
                  required
                  placeholder="e.g., 8471.30.0100"
                  error={!!getFieldError('htsCode')}
                  helperText={getFieldError('htsCode')?.message || getFieldWarning('htsCode')?.message}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Metal Percentage - Show if required or if already has value */}
        {(requiresMetalPercentage || formData.metalPercentage !== undefined) && (
          <Grid item xs={12}>
            <Paper sx={{ 
              p: 2, 
              borderRadius: tokens.radius.md,
              border: requiresMetalPercentage ? `1px solid ${tokens.status.warning}` : `1px solid ${tokens.border.default}`,
              backgroundColor: requiresMetalPercentage ? tokens.status.warningBg : tokens.bg.hover,
            }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <FactoryIcon sx={{ color: requiresMetalPercentage ? tokens.status.warning : tokens.brand.primary }} />
                <Typography variant="subtitle1" fontWeight={tokens.fontWeight.bold} color={tokens.text.body}>
                  Metal Content
                </Typography>
                {requiresMetalPercentage && (
                  <Typography variant="caption" fontWeight={tokens.fontWeight.semibold} sx={{ color: tokens.status.warning }}>
                    Required for this HTS code/country
                  </Typography>
                )}
              </Stack>
              
              <TextInput
                label="Metal Percentage (%)"
                type="number"
                value={formData.metalPercentage !== undefined ? formData.metalPercentage.toString() : ''}
                handleChangeValue={(val) => handleFieldChange('metalPercentage', val)}
                placeholder="e.g., 85.5"
                helperText="Enter the percentage of steel/aluminum content (0-100). Leave empty to assume 100%."
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { 
                    min: 0, 
                    max: 100, 
                    step: 0.1 
                  }
                }}
                error={!!getFieldError('metalPercentage')}
              />
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Validation Issues */}
      {issues.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <ValidationAlerts 
            product={formData} 
            index={index} 
            requiresMetalPercentage={requiresMetalPercentage} 
          />
        </Box>
      )}
    </Box>
  );
};

export default ExpandedProductForm;